import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PairService } from '../../../services/pair.service';
import { Pair } from '../../../interfaces/pair';
import { MatchingPeriod } from '../../../interfaces/matchingPeriod';
import { ParticipantService } from '../../../services/participant.service';
import { Subject } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Donor } from '../../../interfaces/donor';
import { Recipient } from '../../../interfaces/recipient';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatchingPeriodService } from '../../../services/matchingPeriod.service';

@Component({
  selector: 'app-pairs',
  imports: [ReactiveFormsModule, MatInputModule, MatSelectModule, MatCheckboxModule, MatButtonModule, MatFormFieldModule, CommonModule, MatDialogModule],
  templateUrl: './pair-edit.component.html',
  styleUrl: './pair-edit.component.scss'
})
export class PairEditComponent implements OnInit {
  pairForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private pairService: PairService,
    private matchingPeriodService: MatchingPeriodService,
    private participantService: ParticipantService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<PairEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { currentPair: Pair }
  ) {
    // Assign the incoming item to the local variable
    console.log(data.currentPair);
    this.currentPair = data.currentPair;
  }

  relations: string[] = ['Parent', 'Child', 'Partner', 'Sibling', 'Grandparent', 'Grandchild', 'Nephew', 'Niece', 'Uncle', 'Aunt', 'FamilyNotRelated', 'DonorByMedia', 'NotRelated'];

  pairs: Pair[] = [];
  donors: Donor[] = [];
  recipients: Recipient[] = [];
  currentMatchingPeriod?: MatchingPeriod;
  currentPair!: Pair;
  //Send back new pair to list
  responseSubjectNewPair: Subject<Pair> = new Subject<Pair>();

  ngOnInit(): void {
    this.pairForm = this.fb.group({
      id: [this.currentPair.id],
      donor: [this.currentPair.donor.id, Validators.required],
      recipient: [this.currentPair.recipient.id, Validators.required],
      comments: [this.currentPair.comments],
      active: [this.currentPair.active],
      compatible: [this.currentPair.compatible],
      relationToRecipient: [this.currentPair.relationToRecipient, Validators.required]
    });
    this.loadPairsAndParticipants();
    this.getCurrentMatchingPeriod();
  }

  async getCurrentMatchingPeriod() {
    this.matchingPeriodService.getLatestMatchingPeriod().subscribe((currentMatchingPeriodResult) => {
      if (currentMatchingPeriodResult != null) {
        this.currentMatchingPeriod = currentMatchingPeriodResult;
      }
    });
  }

  private async loadPairsAndParticipants(): Promise<void> {
    // Load pairs first
    await this.loadPairs();

    // Then load and filter participants
    this.loadParticipants();
  }

  private async loadPairs(): Promise<void> {
    return new Promise((resolve) => {
      this.pairService.getAllPairs().subscribe((pairs) => {
        this.pairs = pairs;
        resolve();
      });
    });
  }

  private loadParticipants(): void {
    this.participantService.getAllParticipants().subscribe((participants) => {
      // Extract paired donor and recipient IDs from pairs
      const pairedDonorIds = new Set(this.pairs.map((pair) => pair.donor.id));
      const pairedRecipientIds = new Set(
        this.pairs
          .filter((pair) => pair.recipient !== null) // Only include non-null recipient IDs
          .map((pair) => pair.recipient.id)
      );

      // Filter participants based on pairings
      participants.forEach((participant) => {
        if (participant.type === 'Donor' && !pairedDonorIds.has(participant.id)) {
          this.donors.push(participant as Donor);
        } else if (participant.type === 'Recipient' && !pairedRecipientIds.has(participant.id)) {
          this.recipients.push(participant as Recipient);
        }
      });
    });
  }

  onSubmit(): void {
    if (this.pairForm.valid) {
      //Is necessary to set pair active in backend, if the pair is added to the current matchingPeriod.
      const pairData = this.pairForm.value;

      this.pairService.updatePair(pairData).subscribe({
        next: (response) => {
          console.log('Pair edited:', response);
          this.showNotification('Pair edited successfully!');
          // Return the edited pair with action indicator
          this.dialogRef.close({ action: 'edit', pair: response });
        },
        error: (err) => {
          console.error('Error unpairing:', err);
          const errorMessage = err?.error?.message || 'Failed to unpair. Please try again.';
          this.showNotification(errorMessage);
        }
      });
    } else {
      this.showNotification('Please fill out all required fields correctly.');
    }
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  unpairDonor(): void {
    if (!confirm('Are you sure you want to unpair this pair? The donor will become a solo donor.')) {
      return;
    }

    const reasonRemoval = 'Unpaired by user';

    this.pairService.unpairPair(this.currentPair.id!, reasonRemoval).subscribe({
      next: (response) => {
        console.log('Pair unpaired:', response);
        this.showNotification('Pair unpaired successfully! Donor is now a solo donor.');
        // Return a specific object indicating unpair action
        this.dialogRef.close({ action: 'unpair', pairId: this.currentPair.id });
      },
      error: (err) => {
        console.error('Error unpairing pair:', err);
        this.showNotification('Failed to unpair. Please try again.');
      }
    });
  }
}
